"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarCheck2, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, Mail, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = ["Direct discussion with Yassine Essaadi", "Review of current technical & search blockers", "Actionable roadmap tailored to your business goals", "Zero agency account managers or junior hand-offs"];
  return <section id="consultationcta" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28">
      {/* Subtle decorative background tints */}
      <div className="pointer-events-none absolute -top-32 right-0 size-96 rounded-full bg-secondary/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-0 size-96 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mx-auto max-w-4xl rounded-2xl border border-border bg-card p-8 md:p-14 shadow-lg">
          <div className="flex flex-col items-center text-center">
            <Badge variant="outline" className="mb-4 px-3 py-1 font-medium text-xs text-primary border-primary/20 bg-secondary/30">
              <ShieldCheck className="size-3.5 mr-1" />
              International SEO Consultation
            </Badge>

            <h2 className="font-default text-3xl font-semibold tracking-tight text-card-foreground sm:text-4xl md:text-5xl">
              Ready to improve your search visibility?
            </h2>

            <p className="mt-4 max-w-2xl font-sans text-base md:text-lg text-muted-foreground leading-relaxed">
              Talk directly with me about your website's search performance. We will examine your current position, diagnose critical blockers, and outline clear next steps.
            </p>

            {/* Direct collaboration highlights */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5 w-full max-w-2xl text-left">
              {benefits.map((benefit, i) => <div key={benefit} data-index={i} className="flex items-center gap-2.5 rounded-lg border border-border/70 bg-background/60 px-3.5 py-2.5">
                  <CheckCircle2 className="size-4 shrink-0 text-primary" />
                  <span className="font-sans text-sm text-foreground">{benefit}</span>
                </div>)}
            </div>

            {/* Primary Action Button */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto px-8 py-6 text-base font-medium rounded-lg">
                <Link to="/ContactPage">
                  <CalendarCheck2 className="size-5 mr-2" />
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-6 py-6 text-base font-medium rounded-lg">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>

            {/* Direct Contact Details */}
            <div className="mt-8 pt-8 border-t border-border/80 w-full flex flex-col sm:flex-row items-center justify-center gap-6 text-xs md:text-sm text-muted-foreground">
              <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-2 hover:text-foreground transition-colors">
                <Mail className="size-4 text-primary" />
                <span>contact@ye-consultancy.com</span>
              </a>
              <span className="hidden sm:inline text-border">•</span>
              <a href="tel:+447490984430" className="inline-flex items-center gap-2 hover:text-foreground transition-colors">
                <Phone className="size-4 text-primary" />
                <span>+44 7490984430</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}